import { FC, useEffect, useState } from "react";
import { Avatar, Box, IconButton, Modal, Stack, Typography } from "@mui/material";
import CloseSvg from "@/assets/svg/CloseSvg";
import { styles } from "./stylesReplyCommentModal";
import { CompositeDecorator, EditorState, Modifier, SelectionState } from "draft-js";
import theme from "@/theme";
import CustomToast from "../../CustomToast/CustomToast";
import CommentEditor from "@/pages/CommentDetail/CommentEditor";
import GrayDot from "@/assets/svg/GrayDot";

interface IReplyCommentModalProps {
  open: boolean;
  handleClose: () => void;
  uid: string;
  openReplyModal: any;
  postId?: string;
  setLoadedReplies: React.Dispatch<React.SetStateAction<Record<string, any[]>>>;
  setOpenReplies: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setReplyUid: (uid: string) => void;
  onCommentCreated: () => void;
  isLiked: boolean;
}
const MentionSpan = (props: any) => (
  <span style={{ color: theme.palette.custom.mainBlue }}>{props.children}</span>
);

const findMentionEntities = (contentBlock: any, callback: any) => {
  const text = contentBlock.getText();
  const regex = /@\w+/g;
  let matchArr;
  while ((matchArr = regex.exec(text)) !== null) {
    const start = matchArr.index;
    callback(start, start + matchArr[0].length);
  }
};

const decorator = new CompositeDecorator([
  { strategy: findMentionEntities, component: MentionSpan },
]);

const renderTextWithMentions = (text: string) => {
  const regex = /@\w+/g;
  const parts = text.split(regex);
  const matches = text.match(regex);

  const result: React.ReactNode[] = [];
  let i = 0;

  parts.forEach((part, index) => {
    result.push(<span key={`part-${index}`}>{part}</span>);
    if (matches && matches[i]) {
      result.push(
        <span
          key={`mention-${index}`}
          style={{ color: theme.palette.custom.mainBlue, fontWeight: 500 }}
        >
          {matches[i]}
        </span>
      );
      i++;
    }
  });

  return result;
};

const MAX_LENGTH = 300;

const ReplyCommentModal: FC<IReplyCommentModalProps> = ({
  open,
  handleClose,
  postId,
  openReplyModal,
  setReplyUid,
  onCommentCreated,
  isLiked,
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty(decorator));
  const [toast, setToast] = useState<{ message: string; success: boolean } | null>(null);

  const sendComment = async () => {
    handleClose();
  };

  const handleEmojiClick = (emojiData: any) => {
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();

    const newContentState = Modifier.insertText(contentState, selectionState, emojiData.emoji);

    const newEditorState = EditorState.push(editorState, newContentState, "insert-characters");

    setEditorState(newEditorState);
    setShowPicker(false);
  };

  useEffect(() => {
    if (open && openReplyModal?.handle) {
      const contentState = editorState.getCurrentContent();

      const selection = SelectionState.createEmpty(
        editorState.getCurrentContent().getFirstBlock().getKey()
      );

      const contentWithMention = Modifier.insertText(
        contentState,
        selection,
        `@${openReplyModal.handle} `
      );

      const newEditorState = EditorState.push(editorState, contentWithMention, "insert-characters");
      setEditorState(newEditorState);
    }
  }, [open, openReplyModal]);

  return (
    <>
      {" "}
      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
        />
      )}
      <Modal
        open={open}
        onClose={() => {
          if (showPicker) {
            setShowPicker(false);
          } else {
            handleClose();
          }
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={styles.modal}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            position="relative"
            mb={"30px"}
          >
            <IconButton
              onClick={handleClose}
              size="small"
              aria-label="close"
              sx={{ position: "absolute", left: "-5px" }}
            >
              <CloseSvg />
            </IconButton>
            <Typography
              lineHeight={"100%"}
              fontWeight={600}
              fontSize={"1rem"}
              color={theme.palette.custom.blueGray900}
            >
              Replying to
            </Typography>
          </Stack>
          <Stack direction="row" gap="0.25rem" alignItems="center">
            <Avatar sx={{ width: 58, height: 58, zIndex: 2 }} src={openReplyModal.avatar || ""} />
            <Typography
              lineHeight={"100%"}
              fontWeight={600}
              fontSize={"1rem"}
              letterSpacing={"2%"}
              color={theme.palette.custom.blueGray900}
            >
              {openReplyModal.username}
            </Typography>
            <Typography
              color={theme.palette.custom.blueGray900}
              lineHeight={"100%"}
              fontWeight={600}
              fontSize={"1rem"}
              letterSpacing={"2%"}
            >
              {openReplyModal.handle}
            </Typography>
            <Typography
              color={theme.palette.custom.blueGray600}
              lineHeight={"100%"}
              fontWeight={500}
              fontSize={"0.875rem"}
              letterSpacing={"0%"}
              gap={"2px"}
              display={"flex"}
              alignItems={"center"}
              ml="2px"
            >
              <GrayDot />
              {openReplyModal.time}.
            </Typography>
          </Stack>
          <Typography
            sx={{
              border: "1px solid #F4F5F6",
              borderRadius: "1rem",
              ml: "58px",
              padding: "1rem",
              width: "698px",
              mb: "30px",
              fontWeight: 500,
              fontSize: "1rem",
              lineHeight: "24px",
              letterSpacing: "0%",
              whiteSpace: "pre-line",
            }}
          >
            {renderTextWithMentions(openReplyModal.comment)}
          </Typography>
          <CommentEditor
            postId={postId}
            replyUid={openReplyModal.uid}
            editorState={editorState}
            setEditorState={setEditorState}
            showPicker={showPicker}
            setShowPicker={setShowPicker}
            handleEmojiClick={handleEmojiClick}
            edit={undefined}
            setReplyUid={setReplyUid}
            placeholder="Your reply..."
            onSubmit={sendComment}
            maxLength={MAX_LENGTH}
            variant="reply"
            onCommentCreated={onCommentCreated}
            level={openReplyModal.level + 1}
            isLiked={isLiked}
          />
        </Box>
      </Modal>
    </>
  );
};

export default ReplyCommentModal;
